import { useEffect, useReducer } from 'react'

import type {
  FetchArticlesParams,
  Article,
  FetchArticlesResponse,
} from 'services/content/articles/types'

import {
  formatDailyDate,
  generateCommentsFullPathUrl,
  nextDay,
  parseDateString,
} from 'ui-component-library/utils/helpers'
import * as contentApi from 'services/content'
import * as commentsApi from 'services/comments'
import errorLogging from 'services/error-logging'

function parseCommentsUrl(article: Article) {
  const {
    articleDate,
    articleSource,
    link: { url },
    topicId,
  } = article

  let path = ''
  if (articleSource === 'external') {
    // 'articleDate' is always published the day before the DailyDate it's associated.
    // Temporary fix until 'fullPath' is made available to ArticleList articles.
    // TODO: Update with 'fullPath' from CMS when available
    path = `/${formatDailyDate(nextDay(parseDateString(articleDate)))}`
  } else {
    path = url
  }

  return generateCommentsFullPathUrl({ fullPath: path, topicId })
}

interface ArticleWithCommentsCount extends Article {
  commentsCount?: number
}

const START_FETCH = 'start-fetch'
const ARTICLES_FETCHED = 'articles-fetched'
const COMMENTS_COUNT_FETCHED = 'comments-count-fetched'

type State = {
  isLoading: boolean
  topicIds: string[]
  entities: { [key: string]: ArticleWithCommentsCount }
  lastEvaluated: null | {
    lastEvaluatedKeyId: string
    lastSort: string
  }
}

type Action =
  | { type: typeof START_FETCH }
  | {
      type: typeof COMMENTS_COUNT_FETCHED
      payload: { topicId: string; commentsCount: number }
    }
  | {
      type: typeof ARTICLES_FETCHED
      payload: FetchArticlesResponse
    }

const initialState = {
  isLoading: true,
  topicIds: [],
  entities: {},
  lastEvaluated: null,
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case START_FETCH:
      return {
        ...state,
        isLoading: true,
      }

    case COMMENTS_COUNT_FETCHED: {
      const { topicId, commentsCount } = action.payload

      return {
        ...state,
        entities: {
          ...state.entities,
          [topicId]: {
            ...state.entities[topicId],
            commentsCount,
          },
        },
      }
    }

    case ARTICLES_FETCHED: {
      const { articles, lastEvaluatedKeyId, lastSort } = action.payload
      const { topicIds, entities } = articles.reduce(
        (acc, article) => {
          const { topicId } = article
          acc.topicIds.push(topicId)
          acc.entities[topicId] = article
          return acc
        },
        {
          topicIds: [...state.topicIds],
          entities: { ...state.entities },
        },
      )

      const lastEvaluated =
        lastEvaluatedKeyId && lastSort ? { lastSort, lastEvaluatedKeyId } : null

      return {
        ...state,
        topicIds,
        entities,
        lastEvaluated,
        isLoading: false,
      }
    }
  }
}

function getArticles(state: State) {
  return state.topicIds.map((topicId) => {
    const article = state.entities[topicId]

    return {
      ...article,
      commentsUrl: parseCommentsUrl(article),
    }
  })
}

function getIsLoading(state: State) {
  return !state.lastEvaluated && state.isLoading
}

function getIsLoadingMore(state: State) {
  return state.lastEvaluated ? state.isLoading : false
}

function getHasMore(state: State) {
  return state.lastEvaluated
    ? Boolean(state.lastEvaluated.lastEvaluatedKeyId)
    : false
}

function useArticles(params: FetchArticlesParams) {
  const [state, dispatch] = useReducer(reducer, initialState)

  async function fetchArticles() {
    dispatch({
      type: START_FETCH,
    })

    try {
      const response = await contentApi.fetchArticles(
        state.lastEvaluated ? { ...state.lastEvaluated, ...params } : params,
      )

      dispatch({
        type: ARTICLES_FETCHED,
        payload: response,
      })

      return response.articles.map(({ topicId }) => {
        commentsApi
          .fetchCommentsCount(topicId)
          .then((result) => {
            dispatch({
              type: COMMENTS_COUNT_FETCHED,
              payload: { topicId, commentsCount: result },
            })
          })
          .catch((error) => {
            errorLogging(new Error('error fetching comments count'), {
              error,
            })
          })
      })
    } catch (error) {
      errorLogging(new Error('error fetching articles'), {
        error,
      })
    }
  }

  useEffect(() => {
    fetchArticles()
  }, [])

  return {
    articles: getArticles(state),
    isLoading: getIsLoading(state),
    isLoadingMore: getIsLoadingMore(state),
    onLoadMore: fetchArticles,
    hasMore: getHasMore(state),
  }
}

export default useArticles
